type PlanKey = 'humanit-free-monthly' | 'pro-monthly' | 'unlimited-monthly';

type Environment = 'production' | 'development';

const isServer = typeof window === 'undefined';

const nodeEnv = (process.env.NODE_ENV || 'production').toLowerCase();

const requestedMode = (
  (isServer ? process.env.AIHUMANIT_PRICING_MODE : process.env.NEXT_PUBLIC_AIHUMANIT_PRICING_MODE) ||
  nodeEnv ||
  'production'
).toLowerCase();

const stripeTestKey = (process.env.STRIPE_SECRET_KEY || process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '').toLowerCase();
const usingStripeTestKey = stripeTestKey.includes('_test');

const wantsDevPricing = isServer
  ? requestedMode === 'development' ||
    requestedMode === 'dev' ||
    process.env.AIHUMANIT_USE_DEV_PRICING === 'true' ||
    nodeEnv !== 'production' ||
    usingStripeTestKey
  : requestedMode === 'development' ||
    requestedMode === 'dev' ||
    process.env.NEXT_PUBLIC_AIHUMANIT_USE_DEV_PRICING === 'true' ||
    nodeEnv !== 'production' ||
    usingStripeTestKey;

const devPriceIdsPresent = Boolean(
  process.env.STRIPE_PRICE_ID_HUMANIT_FREE_MONTHLY_DEV ||
  process.env.STRIPE_PRICE_ID_HUMANIT_PRO_MONTHLY_DEV ||
  process.env.STRIPE_PRICE_ID_HUMANIT_UNLIMITED_MONTHLY_DEV
);

const ACTIVE_ENVIRONMENT: Environment = isServer
  ? wantsDevPricing && devPriceIdsPresent ? 'development' : 'production'
  : wantsDevPricing ? 'development' : 'production';

export interface PlanDefinition {
  id: PlanKey;
  name: string;
  credits: number;
  defaultPrice: number;
  devPrice?: number;
  features: string[];
  recommended?: boolean;
  popular?: boolean;
}

const PLAN_DEFINITIONS: PlanDefinition[] = [
  {
    id: 'humanit-free-monthly',
    name: 'Free',
    credits: 1000,
    defaultPrice: 0,
    devPrice: 0,
    features: [
      '1,000 words maximum',
      'AI detection bypass',
      'Instant humanization'
    ]
  },
  {
    id: 'pro-monthly',
    name: 'Pro',
    credits: 50000,
    defaultPrice: 19.99,
    devPrice: 1.99,
    features: [
      '50,000 words/month',
      'AI detection bypass',
      'All detection tools bypassed'
    ],
    recommended: true,
    popular: true
  },
  {
    id: 'unlimited-monthly',
    name: 'Unlimited',
    credits: -1,
    defaultPrice: 39.99,
    devPrice: 3.99,
    features: [
      'Unlimited words',
      'AI detection bypass',
      'Priority processing'
    ]
  }
];

const PRICE_IDS: Record<Environment, Record<PlanKey, string | undefined>> = {
  production: {
    'humanit-free-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_FREE_MONTHLY,
    'pro-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_PRO_MONTHLY,
    'unlimited-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_UNLIMITED_MONTHLY
  },
  development: {
    'humanit-free-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_FREE_MONTHLY_DEV || process.env.STRIPE_PRICE_ID_HUMANIT_FREE_MONTHLY,
    'pro-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_PRO_MONTHLY_DEV || process.env.STRIPE_PRICE_ID_HUMANIT_PRO_MONTHLY,
    'unlimited-monthly': process.env.STRIPE_PRICE_ID_HUMANIT_UNLIMITED_MONTHLY_DEV || process.env.STRIPE_PRICE_ID_HUMANIT_UNLIMITED_MONTHLY
  }
};

const PLAN_PRICES: Record<Environment, Record<PlanKey, number>> = {
  production: PLAN_DEFINITIONS.reduce((acc, plan) => {
    acc[plan.id] = plan.defaultPrice;
    return acc;
  }, {} as Record<PlanKey, number>),
  development: PLAN_DEFINITIONS.reduce((acc, plan) => {
    acc[plan.id] = plan.devPrice ?? plan.defaultPrice;
    return acc;
  }, {} as Record<PlanKey, number>)
};

export function getStripePriceIdForPlan(planId: string, { required = true }: { required?: boolean } = {}): string | undefined {
  const priceId = PRICE_IDS[ACTIVE_ENVIRONMENT][planId as PlanKey];
  if (required && !priceId) {
    throw new Error(`Missing Stripe price ID for plan ${planId} in ${ACTIVE_ENVIRONMENT} mode.`);
  }
  return priceId;
}

export function getPlanPrice(planId: string, fallback?: number): number {
  const price = PLAN_PRICES[ACTIVE_ENVIRONMENT][planId as PlanKey];
  if (typeof price === 'number') {
    return price;
  }
  return typeof fallback === 'number' ? fallback : 0;
}

export function isFreePlan(planId: string): boolean {
  return planId === 'humanit-free-monthly';
}

export function isDevelopmentPricing(): boolean {
  return ACTIVE_ENVIRONMENT === 'development';
}

export interface PlanOption {
  id: PlanKey;
  name: string;
  credits: number;
  features: string[];
  recommended?: boolean;
  popular?: boolean;
  price: number;
  stripePriceId?: string;
}

export interface PlanCatalog {
  environment: Environment;
  plans: PlanOption[];
  freePlan: PlanOption;
}

export function buildPlanCatalog(): PlanCatalog {
  const plans: PlanOption[] = PLAN_DEFINITIONS.map((definition) => {
    const price = getPlanPrice(definition.id, definition.defaultPrice);
    const stripePriceId = getStripePriceIdForPlan(definition.id, {
      required: definition.defaultPrice > 0
    });

    const { devPrice, ...definitionForClient } = definition;

    return {
      ...definitionForClient,
      price,
      stripePriceId
    };
  });

  const freePlan = plans.find((plan) => plan.id === 'humanit-free-monthly');
  if (!freePlan) {
    throw new Error('Free plan definition missing');
  }

  return {
    environment: ACTIVE_ENVIRONMENT,
    plans,
    freePlan
  };
}

export function getPlanDefinitions(): PlanDefinition[] {
  return PLAN_DEFINITIONS;
}
